import moment from "moment";
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import { typeAbsenceIcon } from "../../../config/planningMensuel.config";
import { EtatAbsencesEnum } from "../../../dto/GenApi.iNotPaie.Dto/EtatAbsencesEnum";
import { NiveauValidationEnum } from "../../../dto/GenApi.iNotPaie.Dto/NiveauValidationEnum";
import { getAbsenceActionsState } from "../../../Helper/absenceActionsHelper";
import { listeStatutsHistorique } from "../../../Helper/absencesHelper";
import sortHelper from "../../../Helper/sortHelper";
import { AbsenceActionsState } from "../../../shared/interfaces/absenceActionsState.interface";
import { ISidePanelHistorique } from "../../../shared/interfaces/sidePanelHistorique.interface";

export enum EnumSortType {
    TYPE = "type",
    DATE_DEBUT = "dateDebut",
    DATE_FIN = "dateFin",
    DUREE = "duree",
    STATUT = "statut",
}
export interface AbsenceDisplay {
    id: number,
    codeType: string,
    [EnumSortType.TYPE]: string,
    [EnumSortType.DATE_DEBUT]: string,
    [EnumSortType.DATE_FIN]: string,
    [EnumSortType.DUREE]: string,
    [EnumSortType.STATUT]: EtatAbsencesEnum,
    listeNiveauValideurs: NiveauValidationEnum[],
    poseeParGestionnaire: boolean | undefined,
    couleur: string
}

export interface StatutDisplay {
    statut: EtatAbsencesEnum[],
    classe: string,
    icon: string,
    description: string
}

interface ISortPropTable {
    [key: string]: {
        prop: EnumSortType,
        order: null | "ascending" | "descending"
    }
}

@Component({
    components: {
        SidePanelHistorique: require("../sidePanelHistorique/sidePanelHistorique.vue.html")
    }
})
export default class ListeCongesComponent extends Vue {
    @Prop({ type: Array, required: true, default: [] })
    public listeAbsences!: AbsenceDisplay[]; // Liste des absences à afficher

    @Prop({ type: Boolean, required: true, default: true })
    public loading!: boolean;

    // Regroupement des absences par statut selon un ordre spécifique
    private readonly ordreEtatAbsencesEnum: Record<EtatAbsencesEnum, number> = {
        [EtatAbsencesEnum.EnAttenteValidationGestionnaire]: 1, [EtatAbsencesEnum.EnAttenteValidationResponsableNiveau_1]: 1, [EtatAbsencesEnum.EnAttenteValidationResponsableNiveau_2]: 1,
        [EtatAbsencesEnum.Valide]: 2,
        [EtatAbsencesEnum.Calcule]: 3,
        [EtatAbsencesEnum.Refuse]: 4,
        [EtatAbsencesEnum.EnAttenteAnnulationGestionnaire]: 5, [EtatAbsencesEnum.EnAttenteAnnulationResponsableNiveau_1]: 5, [EtatAbsencesEnum.EnAttenteAnnulationResponsableNiveau_2]: 5,
        [EtatAbsencesEnum.Annule]: 6,
        [EtatAbsencesEnum.Cloture]: 7
    };

	/**
	* Liste des statuts d'absences avec leurs propriétés d'affichage.
	* Chaque objet contient un tableau de statuts, une classe CSS, une icône et une description.
	*/
    public listeStatuts: StatutDisplay[] = listeStatutsHistorique;

    public drawerAbsence: ISidePanelHistorique | null = null; // Absence sélectionnée pour afficher l'historique
    public drawerIsVisible = false; // Indique si le panneau latéral est visible ou non

    /**
     * Tableau des propriétés de tri pour les absences.
     * Chaque clé correspond à un type de tri et contient les informations nécessaires pour trier.
     */
    public typePropTable: ISortPropTable = {
        [EnumSortType.TYPE]: { prop: EnumSortType.TYPE, order: null },
        [EnumSortType.DATE_DEBUT]: { prop: EnumSortType.DATE_DEBUT, order: "descending" },
        [EnumSortType.DATE_FIN]: { prop: EnumSortType.DATE_FIN, order: null },
        [EnumSortType.DUREE]: { prop: EnumSortType.DUREE, order: null },
        [EnumSortType.STATUT]: { prop: EnumSortType.STATUT, order: null }
    }

    /**
     * Émet un événement pour modifier l'absence sélectionnée.
     * @param absence L'absence à modifier.
     */
    public modify(absence: AbsenceDisplay): void {
        this.$emit("modifyAbsence", absence.id);
    }

    /**
     * Retourne le nom d'affichage pour le type d'absence.
     * @param statutCode Le code du statut
     */
    public getStatutDisplay(statutCode: EtatAbsencesEnum): StatutDisplay | null {
        const result = this.listeStatuts.find(s => s.statut.includes(statutCode));

        return result !== undefined ? result : null;
    }

    /**
     * Récupère l'icône à afficher
     */
    public getIcon(codeType: string): string {
        return codeType && typeAbsenceIcon[codeType]
            ? typeAbsenceIcon[codeType]
            : typeAbsenceIcon["DEFAULT"];
    }

    /**
     * Gère le changement d'ordre de tri pour la liste des absences.
     * @param param0 Objet contenant la propriété et l'ordre de tri
     */
    public onSortChange({ prop, order }: { prop: string, order: "ascending" | "descending" | null }): void {
        for (const key in this.typePropTable) {
            this.typePropTable[key].order = key === prop ? order : null;
        }
    }

    /**
     * Trie la liste des absences par leur statut dans un ordre spécifique.
     * L'ordre est défini par le tableau `ordreEtatAbsencesEnum`, qui groupe les absences par leur statut.
     * Chaque groupe est ensuite trié par `dateDebut` en ordre décroissant.
     * @param order - L'ordre de tri des statuts, soit "ascending" ou "descending".
     * @returns La liste triée des absences.
     */
    public sortByStatut(order: "ascending" | "descending"): AbsenceDisplay[] {
        return [...this.listeAbsences].sort((a, b) => {
            const ordreA = this.ordreEtatAbsencesEnum[a.statut];
            const ordreB = this.ordreEtatAbsencesEnum[b.statut];

            // comparaison sur la priorité
            if (ordreA !== ordreB) {
                return order === "ascending" ? ordreA - ordreB : ordreB - ordreA;
            }

            // même priorité → tri antichronologique sur la date (plus récent en premier)
            return moment(b.dateDebut, "DD/MM/YYY").valueOf() - moment(a.dateDebut, "DD/MM/YYY").valueOf();
        });
    }

    /**
     * Trie la liste des absences en fonction de la clé de tri active.
     * @param activeSortKey La clé de tri active
     * @returns La liste triée des absences.
     */
    public sortListAbsences(activeSortKey: string): AbsenceDisplay[] {
        const sortProp = this.typePropTable[activeSortKey];
        if (!sortProp) {
            return [...this.listeAbsences];
        }
        const order = sortProp.order as "ascending" | "descending" | null;

        if (!this.listeAbsences || this.listeAbsences.length === 0) {
            return [];
        }
        if (!order) {
            return [...this.listeAbsences];
        }

        switch (activeSortKey) {
            case EnumSortType.TYPE:
                return sortHelper.sortByKeyAnte(EnumSortType.TYPE, order, [...this.listeAbsences]);
            case EnumSortType.DATE_DEBUT:
                return sortHelper.sortByDateWithKey(EnumSortType.DATE_DEBUT, order, [...this.listeAbsences]);
            case EnumSortType.DATE_FIN:
                return sortHelper.sortByDateWithKey(EnumSortType.DATE_FIN, order, [...this.listeAbsences]);
            case EnumSortType.DUREE:
                return this.sortByDuree(order);
            case EnumSortType.STATUT:
                return this.sortByStatut(order);
            default:
                return [...this.listeAbsences];
        }
    }

    /**
     * Retourne la liste triée des absences en fonction de la clé de tri active.
     */
    public get sortedListAbsences(): AbsenceDisplay[] {
        const activeSortKey = Object.keys(this.typePropTable).find(
            key => this.typePropTable[key].order !== null
        );

        if (!activeSortKey) {
            return [...this.listeAbsences];
        }

        return this.sortListAbsences(activeSortKey);
    }

    /**
     * Calcule l'état des actions disponibles pour une absence via le helper partagé.
     * Utilisé par le template pour afficher/masquer et activer/désactiver les boutons
     * de façon cohérente avec les plannings (BUG-003).
     */
    public getActionsState(absence: AbsenceDisplay): AbsenceActionsState {
        return getAbsenceActionsState({
            etat: absence.statut,
            poseeParGestionnaire: absence.poseeParGestionnaire ?? false,
            listeNiveauValideurs: absence.listeNiveauValideurs,
        });
    }

    /**
     * Emet un signal pour que le parent ouvre une popup de validation
     * @param absence
     */
    public cancel(absence: AbsenceDisplay): void {
        this.$emit("cancelAbsence", absence);
    }

    /**
     * Emet un signal pour que le parent ouvre une popup de validation
     * @param absence
     */
    public deleteCancellationRequest(absence: AbsenceDisplay): void {
        this.$emit("deleteCancellationAbsence", absence);
    }

    /**
    * Ouvre le drawer pour afficher l'historique d'une absence spécifique.
    * @param absence L'absence pour laquelle afficher l'historique.
    */
    public openDrawerHistorique(absence: AbsenceDisplay): void {
        this.drawerAbsence = this.convertAbsenceToDisplay(absence);
        this.drawerIsVisible = true;
    }

    /**
     * Convertit une AbsenceDisplay en ISidePanelHistorique pour le side pannel d'historique
     * @param absence
     * @returns
     */
    private convertAbsenceToDisplay(absence: AbsenceDisplay): ISidePanelHistorique {
        const absenceDisplay: ISidePanelHistorique = {
            id: absence.id,
            type: absence.type,
            statut: absence.statut,
            dateDebut: absence.dateDebut,
            dateFin: absence.dateFin,
        }

        return absenceDisplay;
    }

    /**
     * Convertit une chaîne de durée (format "XjYh", "Xj", ou "Yh") en heures totales pour le tri.
     * @param dureeString - La chaîne de durée à convertir
     * @returns Le nombre total d'heures
     */
    private convertDureeToHours(dureeString: string): number {
        if (!dureeString || dureeString.trim() === "") {
            return 0;
        }

        let totalHours = 0;

        // Extrait les jours (j) et les heures (h) de la chaîne - support des nombres décimaux
        const dayMatch = dureeString.match(/(\d+(?:\.\d+)?)j/);
        const hourMatch = dureeString.match(/(\d+(?:\.\d+)?)h/);

        if (dayMatch) {
            totalHours += parseFloat(dayMatch[1]) * 8; // En supposant 8 heures par jour
        }

        if (hourMatch) {
            totalHours += parseFloat(hourMatch[1]);
        }

        return totalHours;
    }

    /**
     * Trie la liste des absences par durée en heures.
     * @param order - L'ordre de tri des durées, soit "ascending" ou "descending".
     * @returns La liste triée des absences.
     */
    private sortByDuree(order: "ascending" | "descending"): AbsenceDisplay[] {
        return [...this.listeAbsences].sort((a, b) => {
            const dureeA = this.convertDureeToHours(a.duree);
            const dureeB = this.convertDureeToHours(b.duree);

            return order === "ascending" ? dureeA - dureeB : dureeB - dureeA;
        });
    }
}



// WEBPACK FOOTER //
// ./src/components/gestionDesAbsencesV2/listeConge/listeConge.ts